import { Button, Skeleton, toast } from "@/components/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/Dropdown";
import { TRACKERS } from "@/constants/Trackers";
import { usePatientDemographics } from "@/features/Patient/PatientBar/network/usePatientDemographics";
import { trackEvent } from "@/lib/tracking/tracking";
import { CareTeam, Practitioner } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { UserPlus, Users } from "lucide-react";
import { useEffect } from "react";
import { useAddCareMember } from "../../network/useAddCareMember";
import { useAvailablePractitioners } from "../../network/useAvailablePractitioners";
import { useCareTeam } from "../../network/useCareTeam";
import { useOutOfOfficeCoveringProvider } from "../../network/useOutOfOfficeCoveringProvider";
import { Collaborator } from "./Collaborator";

export const Collaborators = () => {
  const { patientId } = useParams({ from: "/patient/$patientId" });

  const { data: careTeamData, isError: isErrorCareTeam } =
    useCareTeam(patientId);
  const { data: practitionersData, isError: isErrorActivePractitioners } =
    useAvailablePractitioners(patientId);
  const { data: demographics, isError: isErrorDemographics } =
    usePatientDemographics(patientId);
  const {
    data: outOfOfficeCoveringProviderData,
    isError: isErrorOutOfOfficeCoveringProvider,
  } = useOutOfOfficeCoveringProvider({
    practitionerId: demographics?.clinical.pcpId || undefined,
  });
  const { mutateAsync } = useAddCareMember(patientId);

  const careteam = new CareTeam(careTeamData?.careteam || {});
  const pcpPractitionerId = demographics?.clinical.pcpId || undefined;
  const pcpPractitionerDisplayName = demographics?.clinical.pcpName || "";

  const addPractitioner = async (practitioner: Practitioner) => {
    try {
      await mutateAsync({ practitionerId: practitioner.id });
    } catch (error) {
      toast.error("Failed to add practitioner");
    }
  };

  useEffect(() => {
    if (
      isErrorActivePractitioners ||
      isErrorCareTeam ||
      isErrorDemographics ||
      isErrorOutOfOfficeCoveringProvider
    ) {
      toast.error("Failed to load collaborators");
    }
  }, [
    isErrorActivePractitioners,
    isErrorCareTeam,
    isErrorDemographics,
    isErrorOutOfOfficeCoveringProvider,
  ]);

  if (!careTeamData || !demographics) {
    return (
      <div className="flex items-center gap-1">
        {Array.from({ length: 3 }).map((_, i) => (
          //  biome-ignore lint/suspicious/noArrayIndexKey: unneeded for loading skeleton
          <Skeleton className="h-6 w-6 rounded-full" key={i} />
        ))}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      {pcpPractitionerId && (
        <Collaborator
          practitionerId={pcpPractitionerId}
          display={pcpPractitionerDisplayName || ""}
          type={"PCP"}
          removable={false}
        />
      )}
      {careteam.activeParticipants.map((collaborator) => {
        return (
          <Collaborator
            practitionerId={collaborator.member?.identifier?.value || undefined}
            key={collaborator.member?.display}
            display={collaborator.member?.display || ""}
            type={"Collaborator"}
            removable={true}
          />
        );
      })}
      {outOfOfficeCoveringProviderData?.coverage
        ?.coveringPractitionerDisplayName && (
        <Collaborator
          practitionerId={
            outOfOfficeCoveringProviderData.coverage.coveringUserId.toString() ||
            undefined
          }
          display={
            outOfOfficeCoveringProviderData.coverage
              .coveringPractitionerDisplayName
          }
          type={"Covering Clinician"}
          removable={false}
        />
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="icon" data-testid="add-collaborator">
            <UserPlus size={16} className="text-link" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>Add Collaborators</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {practitionersData?.licensedClinicians.length && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className="flex w-full items-center">
                <Users size={14} className="mr-1" /> Licensed Clinicians
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent
                sideOffset={16}
                className="max-h-[300px] overflow-y-auto"
              >
                {practitionersData?.licensedClinicians.map((user) => {
                  const practitioner = new Practitioner(user.practitioner);
                  return (
                    <DropdownMenuItem
                      onClick={() => {
                        trackEvent(
                          TRACKERS.ADD_LICENSED_CLINICIAN_COLLABORATOR_TO_CHAT,
                        );
                        addPractitioner(practitioner);
                      }}
                      key={practitioner.id}
                    >
                      {practitioner.fullName}
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          )}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="flex w-full items-center">
              <Users size={14} className="mr-1" /> MAs
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent sideOffset={16}>
              {practitionersData?.mas.map((user) => {
                const practitioner = new Practitioner(user.practitioner);
                return (
                  <DropdownMenuItem
                    onClick={() => {
                      trackEvent(TRACKERS.ADD_MA_COLLABORATOR_TO_CHAT);
                      addPractitioner(practitioner);
                    }}
                    key={practitioner.id}
                  >
                    {practitioner.fullName}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
